import React, { useState, useEffect } from 'react';
import {  Printer, Trash2, Search, X } from 'react-feather';
import axios from 'axios';
import { toast } from 'react-toastify';

const OrderKDSModalDetails = ({ order, onClose, onStatusChange }) => {
    if (!order) return null;

    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    

    // Vérifier et initialiser les structures de données manquantes
    if (!order.categories) order.categories = [];
    if (!order.selected_menus) order.selected_menus = [];
    if (!order.products) order.products = [];
    
    // Vérifier si des produits sont disponibles
    const hasProducts = order.selected_menus?.length > 0 || 
                       order.categories?.some(cat => 
                           (cat.products?.length > 0) || 
                           (cat.extra_products?.length > 0)
                       ) ||
                       order.products?.length > 0;
    

    
    // Si commande en attente de paiement, essayer de charger les détails complets
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);
    const [loadedOrderDetails, setLoadedOrderDetails] = useState(null);
    
    useEffect(() => {
        const fetchOrderDetails = async () => {
            // Ne charger que si c'est une commande en attente sans détails
            if (order.status === 'pending_payment' && !hasProducts) {
                try {
                    setIsLoadingDetails(true);
                    const response = await axios.get(
                        `${import.meta.env.VITE_API_URL}/api/back-office/orders/${order.reference}`
                    );
                    if (response.status === 200 && response.data) {
                        console.log("Détails de commande chargés:", response.data);
                        setLoadedOrderDetails(response.data);
                    }
                } catch (error) {
                    console.error("Erreur lors du chargement des détails:", error);
                } finally {
                    setIsLoadingDetails(false);
                }
            }
        };
        
        fetchOrderDetails();
    }, [order.reference, order.status, hasProducts]);
    
    // Utiliser les détails chargés si disponibles
    const displayOrder = loadedOrderDetails || order;
    
    const getVATDetails = () => {
        const vatDetails = new Map();
        displayOrder.selected_menus?.forEach((menu) => {
            if (menu.vat_id?.code && menu.price && menu.quantity) {
                const rate = parseFloat(menu.vat_id.rate.replace(',', '.'));
                const amount = (menu.price * menu.quantity * rate) / 100;
                const code = menu.vat_id.code;
                vatDetails.set(code, (vatDetails.get(code) || 0) + amount);
            }
        });

        displayOrder.categories?.forEach((category) => {
            category.products?.forEach((product) => {
                if (product.vat_id?.code && product.price && product.quantity) {
                    const rate = parseFloat(product.vat_id.rate.replace(',', '.'));
                    const amount = (product.price * product.quantity * rate) / 100;
                    const code = product.vat_id.code;
                    vatDetails.set(code, (vatDetails.get(code) || 0) + amount);
                }
            });

            category.extra_products?.forEach((product) => {
                if (product.vat_id?.code && product.price && product.quantity) {
                    const rate = parseFloat(product.vat_id.rate.replace(',', '.'));
                    const amount = (product.price * product.quantity * rate) / 100;
                    const code = product.vat_id.code;
                    vatDetails.set(code, (vatDetails.get(code) || 0) + amount);
                }
            });
        });

        return vatDetails;
    };

    const calculateTotalWeight = () => {
        try {
            if (!displayOrder.categories || !Array.isArray(displayOrder.categories)) return 0;
            
            const totalWeight = displayOrder.categories.reduce((total, category) => {
                const productsWeight = category.products && Array.isArray(category.products) 
                    ? category.products.reduce(
                        (sum, product) => sum + (parseInt(product.weight) || 0) * (parseInt(product.quantity) || 1),
                        0
                      )
                    : 0;
                
                const extraProductsWeight = category.extra_products && Array.isArray(category.extra_products)
                    ? category.extra_products.reduce(
                        (sum, product) => sum + (parseInt(product.weight) || 0) * (parseInt(product.quantity) || 1),
                        0
                      )
                    : 0;
                
                return total + productsWeight + extraProductsWeight;
            }, 0);
            
            return totalWeight;
        } catch (error) {
            console.error("Erreur lors du calcul du poids total:", error);
            return 0;
        }
    };

    const formatVATDisplay = () => {
        const vatDetails = getVATDetails();
        const totalVAT = Array.from(vatDetails.values()).reduce((sum, amount) => sum + amount, 0);
        const codes = Array.from(vatDetails.keys()).join(', ');
        return {
            codes: codes || 'N/A',
            total: totalVAT.toFixed(2)
        };
    };

    const handlePrint = async () => {
        try {
            setIsLoading(true);
            
            // Utiliser l'endpoint spécifique pour les commandes en attente
            const endpoint = displayOrder.status === 'pending_payment' 
                ? `${import.meta.env.VITE_API_URL}/api/back-office/pending-orders/${displayOrder.reference}/print-ticket`
                : `${import.meta.env.VITE_API_URL}/api/back-office/orders/${displayOrder.reference}/print-ticket`;
                
            console.log("Impression ticket avec endpoint:", endpoint);
            
            const response = await axios.post(endpoint);

            if (response.status === 200) {
                toast.success('Ticket de cuisine imprimé avec succès !');
            }
        } catch (error) {
            console.error('Erreur lors de l\'impression du ticket:', error);
            toast.error('Erreur lors de l\'impression du ticket');
        } finally {
            setIsLoading(false);
        }
    };

    const handleStatusChange = async (newStatus) => {
        try {
            setIsLoading(true);
            console.log('Tentative de mise à jour du statut:', { orderId: displayOrder.id, newStatus });

            const response = await axios.put(
                `${import.meta.env.VITE_API_URL}/api/back-office/orders/${displayOrder.id}/status`,
                { status: newStatus }
            );

            if (response.status === 200) {
                toast.success('Statut mis à jour avec succès');
                if (onStatusChange) {
                    onStatusChange(response.data.order);
                }
                onClose(); // Fermer la modal après la mise à jour
            }
        } catch (error) {
            console.error('Erreur lors du changement de statut:', error);
            toast.error(error.response?.data?.message || 'Erreur lors du changement de statut');
        } finally {
            setIsLoading(false);
        }
    };

    // Fonction pour supprimer une commande
    const deleteOrder = async () => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette commande ?')) {
            return;
        }

        try {
            // Utiliser le restaurant_id de la commande plutôt que le localStorage
            const restaurantId = displayOrder.restaurant_id || localStorage.getItem('selectedRestaurantId');
            
            const response = await axios.delete(
                `${import.meta.env.VITE_API_URL}/api/back-office/${restaurantId}/orders/${displayOrder.reference}/delete`
            );

            if (response.status === 200) {
                toast.success('Commande supprimée avec succès', {
                    position: 'top-center',
                    autoClose: 1000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: false,
                    draggable: true,
                    theme: 'colored',
                });
                
                // Fermer le modal et mettre à jour la liste
                onClose();
                // Si onStatusChange existe, appeler avec null pour indiquer la suppression
                if (onStatusChange) {
                    onStatusChange(null);
                }
            }
        } catch (error) {
            console.error('Erreur lors de la suppression de la commande:', error);
            
            // Afficher un message plus détaillé en cas d'erreur 403
            const errorMessage = error.response?.status === 403 
                ? 'Vous n\'avez pas les permissions nécessaires pour supprimer cette commande'
                : 'Erreur lors de la suppression de la commande';
                
            toast.error(errorMessage, {
                position: 'top-center',
                autoClose: 1000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: true,
                theme: 'colored',
            });
        }
    };

    // Filtrer les produits par terme de recherche
    const filterProductsBySearch = (items) => {
        if (!searchTerm.trim() || !items || items.length === 0) return items;
        
        return items.filter(item => 
            item.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    };

    const vatInfo = formatVATDisplay();
    const totalWeight = calculateTotalWeight();

    return (
        <div className="p-6 bg-white rounded-lg shadow-lg">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-2xl font-bold">Détails de la Commande #{displayOrder.reference}</h3>
                <div className="flex gap-2">
                    <button
                        onClick={handlePrint}
                        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors flex items-center gap-2"
                        disabled={isLoading}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z" clipRule="evenodd" />
                        </svg>
                        Imprimer
                    </button>
                </div>
            </div>

            <div className="mb-4">
                <h4 className="text-lg font-semibold">Informations Client</h4>
                <p className="text-gray-700">Nom: {displayOrder.delivery.customer.firstname} {displayOrder.delivery.customer.lastname}</p>
                <p className="text-gray-700">Adresse: {displayOrder.delivery.address.address}</p>
                <p className="text-gray-700">Date de livraison: {displayOrder.delivery.date}</p>
                {displayOrder.delivery.customer.phone && (
                    <p className="text-gray-700">Téléphone: <span className="font-medium">{displayOrder.delivery.customer.phone}</span></p>
                )}
                {!displayOrder.delivery.customer.phone && displayOrder.billing?.customer?.phone && (
                    <p className="text-gray-700">Téléphone: <span className="font-medium">{displayOrder.billing.customer.phone}</span></p>
                )}
            </div>
            <div className="mb-4">
                <div className="flex justify-between items-center">
                    <h4 className="text-lg font-semibold">Détails de la Commande</h4>
                    {hasProducts && (
                        <div className="relative w-64">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Search className="h-4 w-4 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                className="block w-full pl-9 pr-9 py-1 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Rechercher un produit"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            {searchTerm && (
                                <button
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                                    onClick={() => setSearchTerm('')}
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    )}
                </div>
                
                {isLoadingDetails && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-md mb-4 flex items-center">
                        <svg className="animate-spin h-5 w-5 mr-3 text-blue-500" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <p className="text-blue-700 font-medium">Chargement des détails de la commande...</p>
                    </div>
                )}
                {!hasProducts && !isLoadingDetails && !loadedOrderDetails && (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md mb-4">
                        <p className="text-yellow-700 font-medium">Aucun détail de produit disponible pour cette commande.</p>
                    </div>
                )}
                
                <div>
                    {/* Afficher les produits du tableau principal si présents */}
                    {displayOrder.products && displayOrder.products.length > 0 && (
                        <div className="mb-4">
                            <h5 className="text-md font-semibold mb-2">Produits</h5>
                            {filterProductsBySearch(displayOrder.products).map((product, index) => (
                                <div key={index} className="mb-2">
                                    <p className="text-gray-700">
                                        {product.quantity || 1}x {product.name} - {product.price}€
                                    </p>
                                </div>
                            ))}
                            {displayOrder.products.length > 0 && filterProductsBySearch(displayOrder.products).length === 0 && (
                                <p className="text-sm text-gray-500 italic">Aucun produit ne correspond à votre recherche</p>
                            )}
                        </div>
                    )}
                    
                    {/* Afficher les menus sélectionnés si présents */}
                    {displayOrder.selected_menus && displayOrder.selected_menus.length > 0 && (
                        <div className="mb-4">
                            <h5 className="text-md font-semibold mb-2">Menus sélectionnés</h5>
                            {filterProductsBySearch(displayOrder.selected_menus).map((menu, index) => (
                                <div key={index} className="mb-2">
                                    <p className="text-gray-700">
                                        {menu.quantity || 1}x {menu.name} - {menu.price}€
                                    </p>
                                </div>
                            ))}
                            {displayOrder.selected_menus.length > 0 && filterProductsBySearch(displayOrder.selected_menus).length === 0 && (
                                <p className="text-sm text-gray-500 italic">Aucun menu ne correspond à votre recherche</p>
                            )}
                        </div>
                    )}
                    
                    {/* Afficher les produits des catégories */}
                    {displayOrder.categories && displayOrder.categories.length > 0 && (
                        <div className="mb-4">
                            {displayOrder.categories.map((category, catIndex) => {
                                // Ne pas afficher les catégories vides
                                let categoryProducts = [];
                                if (category.products && category.products.length > 0) {
                                    categoryProducts = [...categoryProducts, ...category.products];
                                }
                                if (category.extra_products && category.extra_products.length > 0) {
                                    categoryProducts = [...categoryProducts, ...category.extra_products];
                                }
                                
                                // Filtrer les produits de cette catégorie selon le terme de recherche
                                const filteredCategoryProducts = filterProductsBySearch(categoryProducts);
                                
                                // Ne pas afficher les catégories qui n'ont aucun produit correspondant à la recherche
                                if (searchTerm.trim() && filteredCategoryProducts.length === 0) return null;
                                
                                return (
                                    <div key={catIndex} className="mb-4 p-2 border-l-2 border-gray-200 pl-2">
                                        <h5 className="text-md font-semibold mb-2">{category.name || `Catégorie ${catIndex+1}`}</h5>
                                        
                                        {/* Produits standards */}
                                        {category.products && category.products.length > 0 && (
                                            <div>
                                                {filterProductsBySearch(category.products).map((product, prodIndex) => (
                                                    <p key={`prod-${prodIndex}`} className="text-gray-700 ml-2 mb-1">
                                                        {product.quantity || 1}x {product.name} - {product.price}€
                                                    </p>
                                                ))}
                                            </div>
                                        )}
                                        
                                        {/* Produits extras */}
                                        {category.extra_products && category.extra_products.length > 0 && (
                                            <div>
                                                {filterProductsBySearch(category.extra_products).map((product, prodIndex) => (
                                                    <p key={`extra-${prodIndex}`} className="text-gray-700 ml-2 mb-1">
                                                        {product.quantity || 1}x {product.name} - {product.price}€
                                                    </p>
                                                ))}
                                            </div>
                                        )}
                                        
                                        {/* Message si aucun produit ne correspond à la recherche */}
                                        {searchTerm.trim() && categoryProducts.length > 0 && filteredCategoryProducts.length === 0 && (
                                            <p className="text-sm text-gray-500 italic ml-2">Aucun produit ne correspond à votre recherche dans cette catégorie</p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200">
                    {displayOrder.promo_code && (
                        <div className="mb-2">
                            <p className="text-gray-700">
                                <span className="font-semibold">Code promo utilisé :</span> {displayOrder.promo_code}
                            </p>
                            {displayOrder.total_price_with_promo && (
                                <p className="text-gray-700">
                                    <span className="font-semibold">Montant après réduction :</span> {displayOrder.total_price_with_promo}€
                                </p>
                            )}
                        </div>
                    )}
                    <p className="text-gray-700">Total: {displayOrder.total_price}€</p>
                    <p className="text-gray-700">TVA ({vatInfo.codes}): {vatInfo.total}€</p>
                    <p className="text-gray-700">Poids Total: {totalWeight > 1000
                        ? `${Math.floor(totalWeight / 1000)}kg ${totalWeight % 1000}g`
                        : `${totalWeight}g`}</p>
                </div>
            </div>

            <div className="mt-6 flex flex-col gap-4">
                <h4 className="text-lg font-semibold">Changer le statut</h4>
                <div className="flex gap-2 flex-wrap">
                    <button
                        onClick={() => handleStatusChange('paid')}
                        className={`px-4 py-2 rounded text-white bg-green-500 hover:bg-green-600 transition-colors ${displayOrder.status === 'paid' ? 'opacity-50 cursor-not-allowed' : ''}`}
                        disabled={isLoading || displayOrder.status === 'paid'}
                    >
                        Payée
                    </button>
                    <button
                        onClick={() => handleStatusChange('processing')}
                        className={`px-4 py-2 rounded text-white bg-blue-500 hover:bg-blue-600 transition-colors ${displayOrder.status === 'processing' ? 'opacity-50 cursor-not-allowed' : ''}`}
                        disabled={isLoading || displayOrder.status === 'processing'}
                    >
                        En préparation
                    </button>
                    <button
                        onClick={() => handleStatusChange('completed')}
                        className={`px-4 py-2 rounded text-white bg-purple-500 hover:bg-purple-600 transition-colors ${displayOrder.status === 'completed' ? 'opacity-50 cursor-not-allowed' : ''}`}
                        disabled={isLoading || displayOrder.status === 'completed'}
                    >
                        Terminée
                    </button>
                    <button
                        onClick={() => handleStatusChange('declined')}
                        className={`px-4 py-2 rounded text-white bg-red-500 hover:bg-red-600 transition-colors ${displayOrder.status === 'declined' ? 'opacity-50 cursor-not-allowed' : ''}`}
                        disabled={isLoading || displayOrder.status === 'declined'}
                    >
                        Refuser
                    </button>
                </div>
            </div>

            <div className="mt-6 flex justify-end">
                <button
                    onClick={onClose}
                    className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors"
                >
                    Fermer
                </button>
            </div>

            <div className="flex justify-between mt-8">
                <button
                    onClick={handlePrint}
                    className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                    <Printer className="mr-2" size={16} />
                    Imprimer le ticket
                </button>
                
                <button
                    onClick={deleteOrder}
                    className="flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                    <Trash2 className="mr-2" size={16} />
                    Supprimer
                </button>
            </div>
        </div>
    );
};

export default OrderKDSModalDetails; 